import { GetOptions, QueryConstraint, SalesOrder } from "@/core";
import { repositoryHost } from "@/repositories";
import { serviceHost } from "@/services";
import { useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { getNextPageParam } from "../utils/page-params";
import { fetchAllPages } from "../utils/fetch-all-pages";

const databaseService = serviceHost.getDatabaseService();
const functionsService = serviceHost.getFunctionsService();
const salesOrderRepository = repositoryHost.getSalesOrderRepository(databaseService);

export const useSalesOrders = (options: GetOptions<SalesOrder>) => {
  return useInfiniteQuery({
    queryKey: ["sales-orders", "get", JSON.stringify(options)],
    queryFn: ({ pageParam }) =>
      salesOrderRepository.getAll({
        ...options,
        pagination: {
          ...options.pagination,
          cursor: pageParam,
        },
      }),
    initialPageParam: "",
    getNextPageParam: getNextPageParam(options),
  });
};

const ALL_SALES_ORDERS_PAGE_SIZE = 300;

/** Loads ALL sales orders by auto-paging. Lazy via `enabled` (see useAllPurchaseOrders). */
export const useAllSalesOrders = (enabled = true) => {
  const query = useInfiniteQuery({
    queryKey: ["sales-orders", "all"],
    enabled,
    queryFn: ({ pageParam }) =>
      salesOrderRepository.getAll({
        orderBy: { field: "createdAt", direction: "desc" },
        pagination: { limit: ALL_SALES_ORDERS_PAGE_SIZE, cursor: pageParam },
      }),
    initialPageParam: "",
    getNextPageParam: getNextPageParam({
      pagination: { limit: ALL_SALES_ORDERS_PAGE_SIZE },
    }),
  });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    if (enabled && hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [enabled, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const salesOrders = useMemo(
    () => query.data?.pages.flat() ?? [],
    [query.data],
  );

  return {
    salesOrders,
    isLoading: enabled && (query.isLoading || query.hasNextPage),
  };
};

/**
 * Auto-pages every sales order matching `queryConstraints`, newest first.
 *
 * Exports use this instead of the table's in-memory rows so the file covers the
 * whole selected range rather than just the first loaded page.
 */
export const fetchAllSalesOrders = (
  queryConstraints: QueryConstraint<keyof SalesOrder & string>[] = [],
): Promise<SalesOrder[]> =>
  fetchAllPages<SalesOrder>((options) => salesOrderRepository.getAll(options), {
    queryConstraints,
    orderBy: { field: "createdAt", direction: "desc" },
  });

/** Fetches a set of sales orders by id (deduped) and returns a map. */
export const useSalesOrdersByIds = (ids: string[]) => {
  const idsKey = Array.from(new Set(ids.filter(Boolean)))
    .sort()
    .join(",");
  const uniqueIds = useMemo(() => (idsKey ? idsKey.split(",") : []), [idsKey]);

  const queries = useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: ["sales-orders", "get-by-id", id],
      queryFn: () => salesOrderRepository.get({ id }),
      enabled: id.length > 0,
    })),
  });

  const map = new Map<string, SalesOrder>();
  queries.forEach((query, index) => {
    if (query.data) map.set(uniqueIds[index], query.data);
  });
  return map;
};

export const useSalesOrderById = (soId: string) => {
  return useQuery({
    queryKey: ["sales-orders", "get-by-id", soId],
    enabled: soId.length > 0,
    queryFn: () => salesOrderRepository.get({ id: soId }),
  });
};

export const useSalesOrdersByClientId = (clientId: string) => {
  return useQuery({
    queryKey: ["sales-orders", "get-by-client-id", clientId],
    enabled: clientId.length > 0,
    queryFn: () =>
      salesOrderRepository.getAll({
        queryConstraints: [
          { field: "clientId", operator: "==", value: clientId },
        ],
        pagination: { limit: 500 },
        orderBy: { field: "createdAt", direction: "desc" },
      }),
  });
};

export const useSalesOrdersByInventoryId = (inventoryId: string) => {
  return useQuery({
    queryKey: ["sales-orders", "get-by-inventory-id", inventoryId],
    enabled: inventoryId.length > 0,
    queryFn: () =>
      salesOrderRepository.getAll({
        queryConstraints: [
          { field: "inventoryId", operator: "==", value: inventoryId },
        ],
        pagination: { limit: 500 },
        orderBy: { field: "createdAt", direction: "desc" },
    }),
  });
};

export const useUpdateSalesOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: {
      soId: string;
      data: Partial<{
        status: "draft" | "allocated" | "open_booking" | "ticketed" | "delivered" | "cancelled";
        flightDate?: string;
        bookingEndDate?: string;
        bookingTbd?: boolean;
        purchaseDate?: string;
        totalMilesSold?: number;
        revenue?: number;
        taxCost?: number;
        salesChannel?: "hubspot" | "manual" | "agent" | "other";
        notes?: string;
        specialAttributeIds?: string[];
        transferProductId?: string;
        clientId?: string;
        soldByUserId?: string;
      }>;
    }) =>
      functionsService.updateSalesOrder(payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["sales-orders"] });
      queryClient.invalidateQueries({
        queryKey: ["sales-orders", "get-by-id", variables.soId],
      });
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["inventories"] });
      queryClient.invalidateQueries({ queryKey: ["audit-logs"] });
    },
  });
};
